import { useRef, useState, type ChangeEvent } from 'react';
import Button from '@/components/base/Button';
import { formatNumber } from '@/lib/format';
import { readFileAsDataUrl } from '@/lib/files';

interface LogoUploadFieldProps {
  id: string;
  name: string;
  /** The currently shown preview (approved logo or a newly chosen one). */
  preview: string | null;
  onChange: (dataUrl: string | null) => void;
  accept: string;
  maxSizeMB: number;
  hint?: string;
  error?: string;
  labels: {
    choose: string;
    replace: string;
    remove: string;
    none: string;
    tooLarge: string;
    wrongType: string;
  };
}

/** Square logo picker with in-memory preview (never persisted). */
export default function LogoUploadField({
  id,
  name,
  preview,
  onChange,
  accept,
  maxSizeMB,
  hint,
  error,
  labels,
}: LogoUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const handleSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    setInternalError(null);
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const allowed = accept
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean);
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (allowed.length && !allowed.includes(ext)) {
      setInternalError(labels.wrongType);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setInternalError(labels.tooLarge.replace('{size}', formatNumber(maxSizeMB)));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    try {
      onChange(await readFileAsDataUrl(file));
    } catch {
      setInternalError(labels.wrongType);
    }
  };

  const shownError = error || internalError || undefined;

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-300 bg-background-100">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <i className="ri-image-2-line text-3xl text-foreground-400" aria-hidden="true" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="file"
            accept={accept}
            className="sr-only"
            onChange={handleSelect}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon="ri-upload-2-line"
              onClick={() => inputRef.current?.click()}
            >
              {preview ? labels.replace : labels.choose}
            </Button>
            <Button
              variant="danger-outline"
              size="sm"
              icon="ri-delete-bin-line"
              onClick={() => {
                onChange(null);
                setInternalError(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              {labels.remove}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-foreground-500">
            {shownError ? <span className="text-rose-600">{shownError}</span> : hint}
          </p>
        </div>
      </div>
    </div>
  );
}